import { useEffect, useRef } from "react";
import { Save, X } from "lucide-react";
import { enterToNext } from "../../lib/focusNext";

const inputCls = (err) => `w-full px-3 py-2 text-sm border rounded-md ${err ? "border-red-500 ring-1 ring-red-500" : "border-slate-300"}`;
const SMALL_BTN = "px-2.5 py-1 rounded-md text-[11px] font-semibold border border-slate-300 bg-white hover:bg-slate-50";

function MultiSelectField({ f, form, setForm, opts, testid }) {
  const arr = Array.isArray(form[f.key]) ? form[f.key] : [];
  return (
    <div className="space-y-1.5">
      {opts.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500" data-testid={`${testid}-count`}>{arr.length} / {opts.length} seçili</span>
          <button type="button" data-testid={`${testid}-select-all`} onClick={() => setForm({ ...form, [f.key]: opts.map((o) => o.value) })} className={`${SMALL_BTN} text-slate-700`}>Tümünü Seç</button>
          <button type="button" data-testid={`${testid}-clear`} onClick={() => setForm({ ...form, [f.key]: [] })} className={`${SMALL_BTN} text-slate-500`}>Temizle</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {opts.map((o) => {
          const on = arr.includes(o.value);
          return (
            <button type="button" key={o.value} data-testid={`${testid}-${o.value}`}
              onClick={() => setForm({ ...form, [f.key]: on ? arr.filter((x) => x !== o.value) : [...arr, o.value] })}
              className={`px-2.5 py-1.5 rounded-md text-xs border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
              {o.label}
            </button>
          );
        })}
        {opts.length === 0 && <span className="text-xs text-slate-400 py-1.5">Önce proje seçin</span>}
      </div>
    </div>
  );
}

function SelectField({ f, value, error, onChange, opts, testid }) {
  return (
    <select required={f.required !== false} data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls(error)}>
      <option value="">Seçiniz…</option>
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// hatalı alanda Tab / odak değişimi engellenir (İptal/Kaydet butonları hariç)
function TextField({ f, value, error, onChange, testid }) {
  return (
    <input required={f.required !== false} type={f.type || "text"} step={f.type === "number" ? "any" : undefined} data-testid={testid}
      value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if ((e.key === "Tab" || e.key === "Enter") && error) e.preventDefault(); }}
      onBlur={(e) => {
        if (!error) return;
        if (e.relatedTarget && e.relatedTarget.tagName === "BUTTON") return;
        const el = e.target;
        setTimeout(() => el.focus(), 0);
      }}
      className={inputCls(error)} />
  );
}

function Field({ f, crud, testidPrefix }) {
  const { form, setForm, errors, optsOf, updateField } = crud;
  const testid = `${testidPrefix}-field-${f.key}`;
  const error = errors[f.key];
  let control;
  if (f.type === "multiselect") control = <MultiSelectField f={f} form={form} setForm={setForm} opts={optsOf(f)} testid={testid} />;
  else if (f.type === "select") control = <SelectField f={f} value={form[f.key]} error={error} onChange={(v) => updateField(f, v)} opts={optsOf(f)} testid={testid} />;
  else control = <TextField f={f} value={form[f.key]} error={error} onChange={(v) => updateField(f, v)} testid={testid} />;
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{f.label}</label>
      {control}
      {error && <div className="text-[11px] text-red-600 font-medium" data-testid={`${testid}-error`}>{error}</div>}
    </div>
  );
}

// CrudTable ekle/düzenle formu — Enter/Tab sağa doğru sıradaki kutuya, son kutudan Kaydet'e; ESC kapatır; düzenlemede ilgili satırın hemen altında açılır
export default function CrudForm({ fields, crud, testidPrefix, inline = false }) {
  const { submit, cancel, errors, editing } = crud;
  const formRef = useRef(null);
  const cancelRef = useRef(cancel); cancelRef.current = cancel;
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape" && !e.defaultPrevented) { e.preventDefault(); e.stopPropagation(); cancelRef.current(); } };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, []);
  useEffect(() => { const first = formRef.current?.querySelector("input:not([type=hidden]), select"); first?.focus(); }, [editing]);
  return (
    <form ref={formRef} onSubmit={submit} onKeyDown={enterToNext} title="Enter / Tab: sıradaki kutu · ESC: kapat" className={`bg-white space-y-4 ${inline ? "border-l-4 border-orange-400 bg-orange-50/30 p-4 rounded-md" : "border border-slate-200 rounded-lg p-5"}`} data-testid={`${testidPrefix}-form`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {fields.map((f) => <Field key={f.key} f={f} crud={crud} testidPrefix={testidPrefix} />)}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={cancel} tabIndex={-1} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900" data-testid={`${testidPrefix}-cancel`}><X className="w-4 h-4" /> İptal <kbd className="ml-1 px-1 rounded border border-slate-300 text-[10px] text-slate-500">ESC</kbd></button>
        <button type="submit" disabled={Object.values(errors).some(Boolean)} data-testid={`${testidPrefix}-save`} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"><Save className="w-4 h-4" /> Kaydet</button>
      </div>
    </form>
  );
}
